import * as React from "react";
import styled from "styled-components";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { Messages } from "views/Constants";
import { PaginatedList } from "components/common";
import MessageTable from "views/components/widgets/MessageTable";
import ErrorWidget from "views/components/widgets/ErrorWidget";
import WindowDimensionsContextProvider from "contexts/WindowDimensionsContextProvider";
import { InputsActions } from "stores/inputs/InputsStore";
import useActiveQueryId from "views/hooks/useActiveQueryId";
import useCurrentSearchTypesResults from "views/components/widgets/useCurrentSearchTypesResults";
import useViewsDispatch from "views/stores/useViewsDispatch";
import reexecuteSearchTypes from "views/components/widgets/reexecuteSearchTypes";
import useOnSearchExecution from "views/hooks/useOnSearchExecution";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import useSearchResult from "views/hooks/useSearchResult";
import RenderCompletionCallback from "./RenderCompletionCallback";
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .pagination {
    margin-bottom: 0;
  }
`;
const useResetPaginationOnSearchExecution = (setCurrentPage, currentPage) => {
  const resetPagination = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [currentPage, setCurrentPage]);
  useOnSearchExecution(resetPagination);
};
const useResetScrollPositionOnPageChange = (currentPage) => {
  const scrollContainerRef = useRef();
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPage, scrollContainerRef]);
  return scrollContainerRef;
};
const useRenderCompletionCallback = () => {
  const renderCompletionCallback = useContext(RenderCompletionCallback);
  useEffect(() => {
    InputsActions.list().then(() => renderCompletionCallback && renderCompletionCallback());
  }, [renderCompletionCallback]);
};
const usePageErrors = (searchTypeId) => {
  var _a, _b;
  const searchResult = useSearchResult();
  const errors = (_b = (_a = searchResult == null ? void 0 : searchResult.result) == null ? void 0 : _a.errors) != null ? _b : [];
  return errors.filter((error) => error.searchTypeId === searchTypeId);
};
const MessageList = ({
  config,
  data: { id: searchTypeId, messages, total: totalMessages },
  fields,
  onConfigChange = () => Promise.resolve(),
  pageSize = Messages.DEFAULT_LIMIT,
  setLoadingState
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { stopAutoRefresh } = useAutoRefresh();
  const pageErrors = usePageErrors(searchTypeId);
  const activeQueryId = useActiveQueryId();
  const searchTypes = useCurrentSearchTypesResults();
  const scrollContainerRef = useResetScrollPositionOnPageChange(currentPage);
  const dispatch = useViewsDispatch();
  useResetPaginationOnSearchExecution(setCurrentPage, currentPage);
  useRenderCompletionCallback();
  const handlePageChange = useCallback(
    (newCurrentPage) => {
      const { effectiveTimerange } = searchTypes[searchTypeId];
      const searchTypePayload = {
        [searchTypeId]: {
          limit: pageSize,
          offset: pageSize * (newCurrentPage - 1)
        }
      };
      stopAutoRefresh();
      setLoadingState(true);
      setCurrentPage(newCurrentPage);
      dispatch(reexecuteSearchTypes(searchTypePayload, effectiveTimerange)).then(() => {
        setLoadingState(false);
      });
    },
    [dispatch, pageSize, searchTypeId, searchTypes, setLoadingState, stopAutoRefresh]
  );
  const onSortChange = useCallback(
    (newSort) => {
      const newConfig = config.toBuilder().sort(newSort).build();
      return onConfigChange(newConfig);
    },
    [config, onConfigChange]
  );
  return /* @__PURE__ */ React.createElement(WindowDimensionsContextProvider, null, /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      onChange: handlePageChange,
      activePage: Number(currentPage),
      showPageSizeSelect: false,
      totalItems: totalMessages,
      pageSize,
      enforcePageBounds: false,
      useQueryParameter: false
    },
    pageErrors.length > 0 ? /* @__PURE__ */ React.createElement(ErrorWidget, { errors: pageErrors }) : /* @__PURE__ */ React.createElement(
      MessageTable,
      {
        activeQueryId,
        config,
        scrollContainerRef,
        fields,
        onSortChange,
        setLoadingState,
        messages
      }
    )
  )));
};
export default MessageList;
